/**
 * Auto-wrapped form renderer for `render_ui` form specs.
 *
 * When a render_ui spec is a form (its submit button fires `submitFormData`),
 * we do NOT hand it to the generic json-render `Renderer`. Instead we parse the
 * spec into an ordered field list + steps and render it in the polished,
 * multi-step card design the embedded widget used before (the retired
 * ShowFormCard) — regardless of how the model laid the spec out.
 *
 * Robustness: every key the submit button lists in `required` is guaranteed a
 * rendered input. If the model references a field it forgot to define (e.g. a
 * dangling date field), we SYNTHESISE it here so the form is completable
 * instead of blocking submit forever.
 *
 * @module lib/json-render/form-auto-card
 */
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { getEmbedLocale } from '@/lib/embed-locale'
import { getLegalString } from './legal-strings'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'

// ── Field model (plain strings — the model already localised its labels) ─────

type AutoKind = 'text' | 'textarea' | 'phone' | 'date' | 'options' | 'multi' | 'consent'

interface AutoField {
  key: string
  kind: AutoKind
  label: string
  required: boolean
  options?: string[]
  placeholder?: string
  consentText?: string
  step: number
}

interface ParsedForm {
  title: string
  fields: AutoField[]
  stepCount: number
  notice?: string
}

// ── Spec parsing ─────────────────────────────────────────────────────────────

const INPUT_TYPES = new Set(['Input', 'Textarea', 'Select', 'Radio', 'Checkbox', 'Switch'])

type El = {
  type?: string
  props?: Record<string, unknown>
  children?: string[]
  on?: Record<string, { action?: string; params?: Record<string, unknown> }>
}

/** Title-case a snake/kebab key for a fallback label. */
function humanize(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

/**
 * The form's display title: the first Heading the model put in the spec, else a
 * humanised form id.
 *
 * Exported because a host that supplies its own chrome (the bubble sheet) needs
 * the same title the card would have shown — deriving it twice is how a header
 * and its body end up disagreeing.
 *
 * @param spec - The decoded render_ui spec
 * @param formId - Submit-button form id, used for the fallback
 * @returns The title to display
 */
export function deriveFormTitle(
  spec: { elements: Record<string, unknown> },
  formId: string,
): string {
  for (const raw of Object.values(spec.elements as Record<string, El>)) {
    if (raw?.type === 'Heading' && raw.props?.text) return String(raw.props.text)
  }
  return humanize(formId)
}

/** Normalises a Select/Radio `options` prop to a flat string list. */
function normalizeOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((o) => {
      if (typeof o === 'string') return o
      if (o && typeof o === 'object') {
        const obj = o as { label?: unknown; value?: unknown }
        return String(obj.label ?? obj.value ?? '')
      }
      return String(o)
    })
    .filter(Boolean)
}

/** Maps one input element to a field (kind inferred from type + name). */
function elementToField(el: El, step: number, required: Set<string>, locale: string): AutoField | null {
  const props = el.props ?? {}
  const name = props.name as string
  if (!name) return null
  const label = (props.label as string) || humanize(name)
  const base = { key: name, label, required: required.has(name), step }

  if (el.type === 'Textarea') {
    return { ...base, kind: 'textarea', placeholder: props.placeholder as string }
  }
  if (el.type === 'Select' || el.type === 'Radio') {
    // A Select with `multiple: true` becomes toggleable chips submitting a string[].
    const kind: AutoKind = el.type === 'Select' && props.multiple === true ? 'multi' : 'options'
    return { ...base, kind, options: normalizeOptions(props.options), placeholder: props.placeholder as string }
  }
  if (el.type === 'Checkbox' || el.type === 'Switch') {
    // Consent-style boolean. A LegalText clause referenced nearby is resolved
    // by the caller; here we just carry the checkbox label.
    return { ...base, kind: 'consent', required: required.has(name) || /consent|agree|privacy|terms/i.test(name) }
  }
  // Input
  const t = String(props.type ?? 'text')
  if (t === 'date') return { ...base, kind: 'date' }
  if (t === 'tel' || /phone|mobile|whatsapp/i.test(name)) {
    return { ...base, kind: 'phone', placeholder: (props.placeholder as string) || '' }
  }
  void locale
  return { ...base, kind: 'text', placeholder: props.placeholder as string }
}

/** Depth-first collect of input + legal-text elements under a subtree. */
function collectSubtree(
  key: string,
  elements: Record<string, El>,
  seen: Set<string>,
): { inputs: El[]; legal: string[] } {
  const inputs: El[] = []
  const legal: string[] = []
  const walk = (k: string) => {
    if (seen.has(k)) return
    seen.add(k)
    const el = elements[k]
    if (!el) return
    if (el.type && INPUT_TYPES.has(el.type) && el.props?.name) inputs.push(el)
    if (el.type === 'LegalText' && el.props?.key) legal.push(String(el.props.key))
    for (const c of el.children ?? []) walk(c)
  }
  walk(key)
  return { inputs, legal }
}

/**
 * Parses a render_ui form spec into an ordered, stepped field list.
 *
 * Steps come from the model's own top-level sections (each direct child of the
 * root that contains real inputs becomes a step). Consent checkboxes are pinned
 * to the final step. Missing `required` fields are synthesised.
 */
/**
 * Wizard sizing — agent-agnostic. A form is split into AT MOST {@link MAX_STEPS}
 * steps; fewer when it has few fields (≈{@link TARGET_PER_STEP} per step). No
 * agent-specific field names or step titles are baked in, so the same logic
 * shapes any agent's form.
 */
const MAX_STEPS = 3
const TARGET_PER_STEP = 4

export function parseFormSpec(
  spec: { root: string; elements: Record<string, unknown> },
  formId: string,
  requiredKeys: string[],
  locale: string,
): ParsedForm {
  const elements = spec.elements as Record<string, El>
  const required = new Set(requiredKeys)
  const root = elements[spec.root]
  const rootChildren = root?.children ?? Object.keys(elements)

  // First Heading in the spec becomes the card title.
  const title = deriveFormTitle({ elements }, formId)

  // Prefer the model's own top-level SECTIONS as wizard steps (each direct child
  // of the root that holds inputs is one section). This keeps related fields
  // together — Contact / Address / Schedule — for whatever form the model built.
  // Loose inputs placed straight on the root collapse into one section.
  const seen = new Set<string>()
  const legal: string[] = []
  const sections: AutoField[][] = []
  let loose: AutoField[] = []
  for (const childKey of rootChildren) {
    const child = elements[childKey]
    if (!child) continue
    if (child.type && INPUT_TYPES.has(child.type) && child.props?.name) {
      const f = elementToField(child, 0, required, locale)
      if (f) loose.push(f)
      seen.add(childKey)
      continue
    }
    const sub = collectSubtree(childKey, elements, seen)
    legal.push(...sub.legal)
    const fs = sub.inputs
      .map((el) => elementToField(el, 0, required, locale))
      .filter((f): f is AutoField => f !== null)
    if (fs.length) sections.push(fs)
  }
  if (loose.length) sections.push(loose)

  const allFields = sections.flat()

  // Synthesise any required key the model never rendered (e.g. a required date)
  // into the most relevant section — date/slot/time → the last section.
  for (const key of requiredKeys) {
    if (allFields.some((f) => f.key === key)) continue
    const kind: AutoKind = /date/i.test(key)
      ? 'date'
      : /phone|mobile/i.test(key)
        ? 'phone'
        : /slot|time|type|kind|category/i.test(key)
          ? 'options'
          : 'text'
    const field: AutoField = { key, kind, label: humanize(key), required: true, step: 0 }
    ;(sections[sections.length - 1] ?? (sections[0] = sections[0] ?? [])).push(field)
    allFields.push(field)
  }

  const consentClause = legal.find((k) => /consent|privacy|contact/i.test(k))
  const consentText = consentClause ? getLegalString(consentClause, locale) : ''

  // Assign steps. Use the model's sections when there are a sensible number
  // (2..MAX_STEPS). Otherwise (one big section, or too many tiny ones) fall back
  // to an even split across at most MAX_STEPS. Fully generic — no form-specific
  // field names anywhere.
  const usable = sections.filter((s) => s.length > 0)
  let stepCount: number
  if (usable.length >= 2 && usable.length <= MAX_STEPS) {
    usable.forEach((s, idx) => s.forEach((f) => (f.step = idx + 1)))
    stepCount = usable.length
  } else {
    const rest = allFields
    stepCount = Math.min(MAX_STEPS, Math.max(1, Math.ceil(rest.length / TARGET_PER_STEP)))
    const perStep = Math.max(1, Math.ceil(rest.length / stepCount))
    rest.forEach((f, i) => (f.step = Math.min(stepCount, Math.floor(i / perStep) + 1)))
  }

  for (const f of allFields) {
    if (f.kind === 'consent') {
      f.step = stepCount
      if (consentText) f.consentText = consentText
    }
  }

  const noticeKey = legal.find((k) => /notice|unconfirmed|accuracy/i.test(k))
  const notice = noticeKey ? getLegalString(noticeKey, locale) : undefined

  // consent fields last within their step (renderField already groups them).
  const ordered = [...allFields.filter((f) => f.kind !== 'consent'), ...allFields.filter((f) => f.kind === 'consent')]

  return {
    title,
    fields: ordered,
    stepCount,
    notice,
  }
}

// ── Card chrome strings ──────────────────────────────────────────────────────

const CARD_STRINGS: Record<
  string,
  { required: string; submit: string; fix: string; back: string; next: string; step: (x: number, y: number) => string }
> = {
  en: {
    required: 'This field is required',
    submit: 'Submit request',
    fix: 'Please complete the highlighted fields',
    back: 'Back',
    next: 'Next',
    step: (x, y) => `Step ${x} of ${y}`,
  },
  ar: {
    required: 'هذا الحقل مطلوب',
    submit: 'إرسال الطلب',
    fix: 'يرجى إكمال الحقول المحددة',
    back: 'رجوع',
    next: 'التالي',
    step: (x, y) => `الخطوة ${x} من ${y}`,
  },
}

const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur'])

// ── Layout ───────────────────────────────────────────────────────────────────

/**
 * Geometry per layout — the ONLY thing `layout` changes. Fields, wizard,
 * validation and copy are identical in both.
 *
 * `inline` is the in-transcript card, unchanged. `panel` fills a host that owns
 * the height budget (the bubble sheet): no max width, no outer border/shadow,
 * and a body that takes the remaining space instead of a viewport fraction.
 *
 * `min-h-0` on the body is load-bearing: without it a flex child refuses to
 * shrink below its content, so the host scrolls instead of the field list.
 */
export const FORM_CARD_LAYOUTS = {
  inline: {
    root: 'mx-auto flex w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm',
    body: 'max-h-[52vh] space-y-3.5 overflow-y-auto px-4 py-3.5',
  },
  panel: {
    root: 'flex h-full w-full flex-col overflow-hidden bg-card',
    body: 'min-h-0 flex-1 space-y-3.5 overflow-y-auto px-4 py-3.5',
  },
} as const

export type FormCardLayout = keyof typeof FORM_CARD_LAYOUTS

// ── Component ────────────────────────────────────────────────────────────────

export interface FormAutoCardProps {
  spec: { root: string; elements: Record<string, unknown> }
  formId: string
  required: string[]
  /** Receives the keyed values object; the caller emits the submission envelope. */
  onSubmit: (values: Record<string, unknown>) => void
  disabled?: boolean
  /**
   * Geometry only (see {@link FORM_CARD_LAYOUTS}). `panel` also drops the card's
   * own title bar, because a host that asks for `panel` supplies its own header
   * and two titles would stack.
   */
  layout?: FormCardLayout
}

/**
 * Renders a render_ui form spec in the polished multi-step card design.
 */
export function FormAutoCard({
  spec,
  formId,
  required,
  onSubmit,
  disabled,
  layout = 'inline',
}: FormAutoCardProps) {
  const locale = getEmbedLocale() ?? 'en'
  const t = CARD_STRINGS[locale] ?? CARD_STRINGS.en
  const isRtl = RTL_LOCALES.has(locale)

  const parsed = useMemo(
    () => parseFormSpec(spec, formId, required, locale),
    [spec, formId, required, locale],
  )

  const initialValues = useMemo(() => {
    const v: Record<string, unknown> = {}
    for (const f of parsed.fields) v[f.key] = f.kind === 'consent' ? false : f.kind === 'multi' ? [] : ''
    // Seed literal prefills the model supplied on the input props. Arrays pass
    // through so a `multiple` field can arrive with options already selected —
    // excluding all objects would drop exactly that case.
    for (const raw of Object.values(spec.elements as Record<string, El>)) {
      const name = raw.props?.name as string | undefined
      const lit = (raw.props?.value ?? raw.props?.checked) as unknown
      if (name === undefined || lit === undefined || lit === null) continue
      if (Array.isArray(lit) || typeof lit !== 'object') v[name] = lit
    }
    return v
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed])

  const [values, setValues] = useState<Record<string, unknown>>(initialValues)
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [triedSubmit, setTriedSubmit] = useState(false)
  const [stepIdx, setStepIdx] = useState(0)

  const set = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => (prev[key] ? { ...prev, [key]: false } : prev))
  }

  const fieldMissing = (f: AutoField, v: unknown): boolean => {
    if (!f.required) return false
    if (f.kind === 'consent') return v !== true
    if (f.kind === 'multi') return !Array.isArray(v) || v.length === 0
    return typeof v !== 'string' || v.trim().length === 0
  }

  const stepCount = parsed.stepCount
  const isMultiStep = stepCount > 1
  const stepFields = parsed.fields.filter((f) => f.step === stepIdx + 1)

  const collectErrors = (list: AutoField[]) => {
    const next: Record<string, boolean> = {}
    for (const f of list) if (fieldMissing(f, values[f.key])) next[f.key] = true
    return next
  }

  const handleBack = () => {
    setErrors({})
    setTriedSubmit(false)
    setStepIdx((i) => Math.max(0, i - 1))
  }
  const handleNext = () => {
    const next = collectErrors(stepFields)
    if (Object.keys(next).length) {
      setTriedSubmit(true)
      setErrors(next)
      return
    }
    setErrors({})
    setTriedSubmit(false)
    setStepIdx((i) => Math.min(stepCount - 1, i + 1))
  }
  const handleSubmit = () => {
    const next = collectErrors(parsed.fields)
    setTriedSubmit(true)
    setErrors(next)
    if (Object.keys(next).length) {
      const firstKey = parsed.fields.find((f) => next[f.key])?.key
      const errStep = parsed.fields.find((f) => f.key === firstKey)?.step
      if (errStep) setStepIdx(errStep - 1)
      return
    }
    onSubmit(values)
  }

  const renderField = (f: AutoField) => {
    const err = errors[f.key]
    const labelEl = (
      <span className="text-[12.5px] font-medium text-foreground">
        {f.label} {f.required && <span className="text-destructive">*</span>}
      </span>
    )
    switch (f.kind) {
      case 'options': {
        const opts = f.options ?? []
        const current = values[f.key] as string
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <div className="flex flex-wrap gap-1.5">
              {opts.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => set(f.key, opt)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-[13px] transition-colors',
                    current === opt
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt}
                </button>
              ))}
            </div>
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
      }
      case 'multi': {
        const opts = f.options ?? []
        const current = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : []
        const toggle = (opt: string) =>
          set(f.key, current.includes(opt) ? current.filter((o) => o !== opt) : [...current, opt])
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <div className="flex flex-wrap gap-1.5">
              {opts.map((opt) => {
                const active = current.includes(opt)
                return (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggle(opt)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-[13px] transition-colors',
                      active
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {opt}
                  </button>
                )
              })}
            </div>
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
      }
      case 'phone':
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <Input
              dir="ltr"
              inputMode="tel"
              value={String(values[f.key] ?? '')}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder || ''}
              className={cn('h-8 text-sm', err && 'border-destructive')}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
      case 'date':
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <Input
              type="date"
              dir="ltr"
              value={String(values[f.key] ?? '')}
              onChange={(e) => set(f.key, e.target.value)}
              className={cn('h-8 text-sm', err && 'border-destructive')}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
      case 'textarea':
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <textarea
              value={String(values[f.key] ?? '')}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              rows={3}
              className={cn(
                'w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring',
                err && 'border-destructive',
              )}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
      case 'consent':
        return (
          <div key={f.key} className="space-y-1">
            {f.consentText && (
              <p className="text-[11.5px] leading-snug text-muted-foreground">{f.consentText}</p>
            )}
            <label className="flex items-start gap-2 text-[12px] leading-snug text-foreground cursor-pointer">
              <button
                type="button"
                role="checkbox"
                aria-checked={values[f.key] === true}
                onClick={() => set(f.key, values[f.key] !== true)}
                className={cn(
                  'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border',
                  values[f.key] === true
                    ? 'border-primary bg-primary text-primary-foreground'
                    : err
                      ? 'border-destructive'
                      : 'border-muted-foreground/50',
                )}
              >
                {values[f.key] === true && <Check className="h-3 w-3" />}
              </button>
              <span>{f.label}</span>
            </label>
            {err && <p className="text-[11.5px] text-destructive ms-6">{t.required}</p>}
          </div>
        )
      default:
        return (
          <div key={f.key} className="space-y-1.5">
            {labelEl}
            <Input
              value={String(values[f.key] ?? '')}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              className={cn('h-8 text-sm', err && 'border-destructive')}
            />
            {err && <p className="text-[11.5px] text-destructive">{t.required}</p>}
          </div>
        )
    }
  }

  const isLastStep = stepIdx === stepCount - 1

  return (
    <div
      data-testid="show-form-card"
      data-layout={layout}
      dir={isRtl ? 'rtl' : 'ltr'}
      className={FORM_CARD_LAYOUTS[layout].root}
    >
      {layout === 'inline' && (
        <div className="border-b border-border px-4 py-3">
          <p data-testid="show-form-title" className="text-sm font-semibold text-foreground">
            {parsed.title}
          </p>
        </div>
      )}

      {isMultiStep && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <div className="flex gap-1" data-testid="show-form-progress">
            {Array.from({ length: stepCount }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === stepIdx ? 'w-5 bg-primary' : i < stepIdx ? 'w-1.5 bg-primary/60' : 'w-1.5 bg-muted-foreground/30',
                )}
              />
            ))}
          </div>
          <span className="text-[11.5px] text-muted-foreground">{t.step(stepIdx + 1, stepCount)}</span>
        </div>
      )}

      <div className={FORM_CARD_LAYOUTS[layout].body}>
        {stepFields.filter((f) => f.kind !== 'consent').map(renderField)}

        {isLastStep && parsed.notice && (
          <div className="rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-[11.5px] leading-snug text-warning">
            ⚠ {parsed.notice}
          </div>
        )}

        {stepFields.filter((f) => f.kind === 'consent').map(renderField)}
      </div>

      <div className="border-t border-border px-4 py-3">
        {triedSubmit && Object.values(errors).some(Boolean) && (
          <p className="mb-2 text-[11.5px] text-destructive">{t.fix}</p>
        )}
        {isMultiStep ? (
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <Button type="button" variant="outline" onClick={handleBack} className="gap-1" data-testid="show-form-back">
                {isRtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                {t.back}
              </Button>
            )}
            {!isLastStep ? (
              <Button type="button" onClick={handleNext} className="flex-1 gap-1" data-testid="show-form-next">
                {t.next}
                {isRtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            ) : (
              <Button className="flex-1" onClick={handleSubmit} disabled={disabled} data-testid="show-form-submit">
                {t.submit}
              </Button>
            )}
          </div>
        ) : (
          <Button className="w-full" onClick={handleSubmit} disabled={disabled} data-testid="show-form-submit">
            {t.submit}
          </Button>
        )}
      </div>
    </div>
  )
}
